// ProductList.tsx
import React, { useState, useEffect } from 'react';
import { Search, Plus, Eye, Edit, Trash2, CheckCircle, XCircle } from 'lucide-react'; 
import { Link, useNavigate } from 'react-router-dom'; 
import axios from 'axios'; 

interface Product {
  id: number; 
  itemId?: number; 
  image: string; 
  name: string; 
  price: number; 
  stock: number; 
  category: string; 
  productType: 'gold' | 'silver';
}

interface Notification {
  message: string;
  type: 'success' | 'error';
}

const ProductList: React.FC = () => {
  const navigate = useNavigate(); 
  const [products, setProducts] = useState<Product[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [productTypeFilter, setProductTypeFilter] = useState('All'); 
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);
  const [notification, setNotification] = useState<Notification | null>(null);

  // --- TS2802 FIX: Using Array.from instead of spread for Set compatibility ---
  const uniqueCategories = ['All', ...Array.from(new Set(products.map(p => p.category)))];

  useEffect(() => {
    fetchProducts();
  }, [productTypeFilter]); 

  const fetchProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      let goldProducts: Product[] = [];
      let silverProducts: Product[] = [];
      const baseUrl = 'https://sardar-jewellers.com/api/admin';

      if (productTypeFilter === 'All' || productTypeFilter === 'gold') {
        const goldResponse = await axios.get<any[]>(`${baseUrl}/gold-products`);
        goldProducts = goldResponse.data.map(goldItem => ({
          id: goldItem.srno,
          itemId: goldItem.itemId,
          image: goldItem.images?.[0]?.imagePath || '',
          name: goldItem.itemName,
          price: goldItem.price,
          stock: goldItem.stockQuantity,
          category: goldItem.category,
          productType: 'gold',
        }));
      }

      if (productTypeFilter === 'All' || productTypeFilter === 'silver') {
        const silverResponse = await axios.get<any[]>(`${baseUrl}/silver-products`);
        silverProducts = silverResponse.data.map(silverItem => ({
          id: silverItem.srno,
          itemId: silverItem.itemId,
          image: silverItem.images?.[0]?.imagePath || '',
          name: silverItem.itemName,
          price: silverItem.price,
          stock: silverItem.stockQuantity,
          category: silverItem.category,
          productType: 'silver',
        }));
      }
      setProducts([...goldProducts, ...silverProducts]);
    } catch (err) {
      setError('Failed to fetch products.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmedDelete = async () => {
    if (!productToDelete) return;
    const { id, productType, name } = productToDelete;
    setIsConfirmOpen(false);

    try {
      await axios.delete(`https://sardar-jewellers.com/api/admin/${productType}-products/${id}`);
      setNotification({ message: `${name} deleted!`, type: 'success' });
      fetchProducts(); 
    } catch (error) {
      setNotification({ message: `Failed to delete ${name}.`, type: 'error' });
    } finally {
      setProductToDelete(null);
      setTimeout(() => setNotification(null), 5000);
    }
  };

  const filteredProducts = products.filter((product) => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      selectedCategory === 'All' || product.category.toLowerCase() === selectedCategory.toLowerCase();
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      {/* Confirmation Modal */}
      {isConfirmOpen && productToDelete && (
        <div className="fixed inset-0 z-50 bg-gray-600 bg-opacity-50 flex justify-center items-center">
          <div className="bg-white rounded-lg p-6 max-w-md w-full m-4">
            <h3 className="text-lg font-bold text-red-600 mb-4">Confirm Deletion</h3>
            <p className="mb-6">Delete <span className="font-bold">{productToDelete.name}</span>?</p>
            <div className="flex justify-end space-x-3">
              <button onClick={() => setIsConfirmOpen(false)} className="px-4 py-2 border rounded-md">Cancel</button>
              <button onClick={handleConfirmedDelete} className="px-4 py-2 bg-red-600 text-white rounded-md">Yes, Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Toast */}
      {notification && (
        <div className={`fixed bottom-5 right-5 p-4 rounded-lg shadow-xl z-50 flex items-center bg-white border-l-4 ${notification.type === 'success' ? 'border-green-500' : 'border-red-500'}`}>
           <p className="font-medium">{notification.message}</p>
        </div>
      )}

      <div className="max-w-7xl mx-auto bg-white rounded-lg shadow-md p-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-bold">Product List</h2>
          <Link to="/admin/products/create" className="px-4 py-2 bg-indigo-600 text-white rounded-md flex items-center">
            <Plus className="h-5 w-5 mr-2" /> Add New Product
          </Link>
        </div>

        <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              className="w-full pl-10 pr-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <select
            className="border rounded-md px-3 py-2"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            {uniqueCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
          </select>

          <select
            className="border rounded-md px-3 py-2"
            value={productTypeFilter}
            onChange={(e) => setProductTypeFilter(e.target.value)}
          >
            <option value="All">All Types</option>
            <option value="gold">Gold</option>
            <option value="silver">Silver</option>
          </select>
        </div>

        <div className="overflow-x-auto border rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 text-xs uppercase font-medium text-gray-500">
              <tr>
                <th className="px-6 py-3 text-left">Image</th>
                <th className="px-6 py-3 text-left">Name</th>
                <th className="px-6 py-3 text-left">Price</th>
                <th className="px-6 py-3 text-left">Stock</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredProducts.map((product) => (
                <tr key={`${product.productType}-${product.id}`}>
                  <td className="px-6 py-4">
                    {/* Fixed Image logic with fallback */}
                    <img
                      className="h-12 w-12 rounded-lg object-cover bg-gray-100"
                      src={product.image}
                      alt={product.name}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://dummyimage.com/150x150/cccccc/000000&text=No+Image';
                      }}
                    />
                  </td>
                  <td className="px-6 py-4 font-medium text-gray-900">{product.name}</td>
                  <td className="px-6 py-4">₹{product.price.toFixed(2)}</td>
                  <td className="px-6 py-4">{product.stock}</td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end space-x-2">
                   <button 
  onClick={() => navigate(`/admin/edit-product/${product.productType}/${product.id}`)}
  className="text-yellow-600 hover:bg-yellow-100 p-2 rounded-full transition"
  title="Edit Product"
>
  <Edit className="w-5 h-5" />
</button>
                      <button
                        onClick={() => { setProductToDelete(product); setIsConfirmOpen(true); }}
                        className="text-red-600 hover:bg-red-100 p-2 rounded-full transition"
                        title="Delete Product"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredProducts.length === 0 && (
            <div className="text-center py-10 text-gray-500">No products found matching your search.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductList;